import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, FlaskConical, RefreshCw } from "lucide-react";
import { Link } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  FIRST_ECHO_PAYGATE_EXPERIMENT_ID,
  fetchExperimentAssignments,
  fetchExperimentSummary,
  formatRate,
  formatRevenue,
  isPaidVariant,
  type ExperimentMetrics,
  variantLabel,
} from "./experimentClient";

const SUMMARY_QUERY_KEY = [
  "experiment-summary",
  FIRST_ECHO_PAYGATE_EXPERIMENT_ID,
] as const;
const ASSIGNMENTS_QUERY_KEY = [
  "experiment-assignments",
  FIRST_ECHO_PAYGATE_EXPERIMENT_ID,
] as const;

import { ExperimentTabs } from "./ExperimentTabs";

export function ExperimentsPage() {
  const queryClient = useQueryClient();
  const summary = useQuery({
    queryKey: SUMMARY_QUERY_KEY,
    queryFn: () => fetchExperimentSummary(),
    staleTime: 30_000,
  });
  const assignments = useQuery({
    queryKey: ASSIGNMENTS_QUERY_KEY,
    queryFn: () => fetchExperimentAssignments(),
    staleTime: 30_000,
  });
  const refreshing = summary.isFetching || assignments.isFetching;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: SUMMARY_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_QUERY_KEY }),
    ]);

  return (
    <div className="space-y-4 p-6">
      <ExperimentTabs />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FlaskConical className="size-5 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Experiments</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            First-Echo paygate assignment, delivery, paywall and payment
            results. Counts come from durable producer event IDs.
          </p>
          <code className="text-xs text-muted-foreground">
            {FIRST_ECHO_PAYGATE_EXPERIMENT_ID}
          </code>
        </div>
        <Button
          variant="outline"
          onClick={() => void refresh()}
          disabled={refreshing}
        >
          <RefreshCw className={refreshing ? "animate-spin" : ""} />
          Refresh
        </Button>
      </div>

      {summary.error || assignments.error ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Experiment data is unavailable</AlertTitle>
          <AlertDescription>
            {String(summary.error || assignments.error)}
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.data?.truncated || assignments.data?.truncated ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>Partial result</AlertTitle>
          <AlertDescription>
            The experiment partition exceeded the API safety limit. Do not use
            these figures for a decision until the projection is compacted.
          </AlertDescription>
        </Alert>
      ) : null}

      {summary.isPending ? (
        <p className="text-sm text-muted-foreground">
          Loading experiment results…
        </p>
      ) : summary.data ? (
        <>
          <MetricCards metrics={summary.data.totals} />
          <VariantTable variants={summary.data.variants} />
        </>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Assignment audit</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Bucket</TableHead>
                <TableHead>First Echo delivered</TableHead>
                <TableHead>Paywall</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Release</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(assignments.data?.data ?? []).map((assignment) => (
                <TableRow key={assignment.assignment_id}>
                  <TableCell>
                    <Link
                      className="font-medium underline-offset-4 hover:underline"
                      to={`/contacts/${encodeURIComponent(assignment.user_id)}/show`}
                    >
                      {assignment.user_id}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <VariantBadge variant={assignment.variant} />
                  </TableCell>
                  <TableCell>
                    {formatTimestamp(assignment.assigned_at)}
                  </TableCell>
                  <TableCell>{assignment.bucket ?? "—"}</TableCell>
                  <TableCell>
                    {yesNo(Boolean(assignment.last_echo_delivered_at))}
                  </TableCell>
                  <TableCell>
                    {yesNo(Boolean(assignment.last_paywall_at))}
                  </TableCell>
                  <TableCell>
                    {yesNo(Boolean(assignment.last_payment_at))}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {shortId(assignment.config_release_id)}
                  </TableCell>
                </TableRow>
              ))}
              {!assignments.isPending && !assignments.data?.data.length ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-center text-muted-foreground"
                  >
                    No eligible contacts have been assigned yet.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          {assignments.data?.has_more ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Showing the newest {assignments.data.data.length} of{" "}
              {assignments.data.total}
              assignments.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

ExperimentsPage.path = "/experiments";

function MetricCards({ metrics }: { metrics: ExperimentMetrics }) {
  const cards = [
    ["Assigned", metrics.assigned_contacts],
    ["First Echo delivered", metrics.echo_delivered_contacts],
    ["Paywalled", metrics.paywall_contacts],
    ["Paid", metrics.paid_contacts],
    ["Net revenue", formatRevenue(metrics.net_revenue_minor, metrics.currency)],
  ] as const;
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      {cards.map(([label, value]) => (
        <Card key={label}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {label}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold">{value}</CardContent>
        </Card>
      ))}
    </div>
  );
}

function VariantTable({ variants }: { variants: ExperimentMetrics[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Variant comparison</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Variant</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Delivered</TableHead>
              <TableHead>Paywall contacts</TableHead>
              <TableHead>Paywall views</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Paywall → paid</TableHead>
              <TableHead>Net revenue</TableHead>
              <TableHead>Refunds / disputes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {variants.map((variant) => (
              <TableRow key={variant.variant}>
                <TableCell>
                  <VariantBadge variant={variant.variant} />
                </TableCell>
                <TableCell>{variant.assigned_contacts}</TableCell>
                <TableCell>{variant.echo_delivered_contacts}</TableCell>
                <TableCell>{variant.paywall_contacts}</TableCell>
                <TableCell>{variant.paywall_views}</TableCell>
                <TableCell>{variant.paid_contacts}</TableCell>
                <TableCell>{formatRate(variant.paywall_to_paid_pct)}</TableCell>
                <TableCell>
                  {formatRevenue(variant.net_revenue_minor, variant.currency)}
                </TableCell>
                <TableCell>
                  {variant.refund_events + variant.partial_refund_events} /{" "}
                  {variant.dispute_events}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function VariantBadge({ variant }: { variant: string }) {
  return (
    <Badge variant={isPaidVariant(variant) ? "default" : "secondary"}>
      {variantLabel(variant)}
    </Badge>
  );
}

function yesNo(value: boolean) {
  return value ? "Yes" : "No";
}

function formatTimestamp(value?: string) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function shortId(value?: string) {
  if (!value) return "—";
  return value.length > 14 ? `${value.slice(0, 12)}…` : value;
}
